/**
 * Sub-components and utilities used by AccountFormWizard. Extracted to a
 * sibling file to keep AccountFormWizard.tsx under the max-lines threshold.
 * Not part of the public API; the wizard is the only intended consumer.
 */

import { useState, useRef, useEffect, type CSSProperties, type RefObject } from 'react'

import { Button, Input, FormField, Skeleton } from '@elsapiens/ui'
import { CheckCircle2 } from 'lucide-react'
import { createPortal } from 'react-dom'

import { normalizeTag, getBankCodeLabel, type AccountFormData } from './chart-of-accounts-helpers'

import type { Account } from '../../services/account-service'

// ── KbdHint ────────────────────────────────────────────────────────────────────

export function KbdHint({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="inline-flex items-center px-1 py-px text-[10px] font-mono border border-border rounded bg-muted text-muted-foreground mx-0.5">
      {children}
    </kbd>
  )
}

// ── TypeCard ──────────────────────────────────────────────────────────────────

interface TypeCardProps {
  label: string
  description: string
  icon: React.ReactNode
  isSelected: boolean
  onClick: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  tabIndex?: number
  role?: string
  shortcutKey?: string
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'aria-checked'?: boolean
}

export function TypeCard({
  label, description, icon, isSelected, onClick, onKeyDown,
  tabIndex, role, shortcutKey, 'aria-checked': ariaChecked,
}: TypeCardProps): JSX.Element {
  return (
    <button
      aria-checked={ariaChecked}
      className={`el-edge flex items-center gap-3 p-3 rounded-lg border text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/40 hover:bg-muted/40'
      }`}
      role={role ?? 'radio'}
      tabIndex={tabIndex ?? (isSelected ? 0 : -1)}
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <TypeCardIcon icon={icon} isSelected={isSelected} />
      <div className="flex-1 min-w-0">
        <TypeCardTitle isSelected={isSelected} label={label} shortcutKey={shortcutKey} />
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{description}</p>
      </div>
      {isSelected && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
    </button>
  )
}

function TypeCardIcon({ icon, isSelected }: { icon: React.ReactNode; isSelected: boolean }): JSX.Element {
  return (
    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
      isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
    }`}>
      {icon}
    </div>
  )
}

function TypeCardTitle({ label, isSelected, shortcutKey }: { label: string; isSelected: boolean; shortcutKey?: string }): JSX.Element {
  return (
    <p className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
      {label}
      {shortcutKey && (
        <span className="ml-2 px-1 py-0 text-[10px] border border-border rounded font-mono text-muted-foreground align-middle">
          {shortcutKey}
        </span>
      )}
    </p>
  )
}

// ── TagsField ─────────────────────────────────────────────────────────────────

interface TagsFieldProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  tagInput: string
  setTagInput: (v: string) => void
}

export function TagsField({ formData, setFormData, tagInput, setTagInput }: TagsFieldProps): JSX.Element {
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const value = normalizeTag(tagInput)
      if (value && !formData.tags.includes(value)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, value] }))
      }
      setTagInput('')
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      setFormData(prev => ({ ...prev, tags: prev.tags.slice(0, -1) }))
    }
  }

  return (
    <FormField helperText="Press Enter or comma to add a tag." label="Custom Tags">
      <div className="el-edge flex flex-wrap items-center gap-1.5 min-h-[2.25rem] px-3 py-1.5 rounded-md border border-input bg-background">
        {formData.tags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            {tag}
            <button
              className="ml-0.5 hover:text-destructive"
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          placeholder={formData.tags.length === 0 ? 'e.g., vendor_payment, office_supplies' : 'Add tag…'}
          type="text"
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
    </FormField>
  )
}

// ── SiblingsPopup ─────────────────────────────────────────────────────────────

function useSiblingsPopupPosition(isOpen: boolean, btnRef: RefObject<HTMLButtonElement>, popupRef: RefObject<HTMLDivElement>, onClose: () => void): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({})

  useEffect(() => {
    if (!isOpen) {return}
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) {setStyle({ position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 9999 })}
    function onPointerDown(e: PointerEvent): void {
      if (!btnRef.current?.contains(e.target as Node) && !popupRef.current?.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return style
}

function filterSiblings(siblings: Account[], query: string): Account[] {
  const q = query.trim().toLowerCase()
  if (!q) {return siblings}
  return siblings.filter(s => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q))
}

function SiblingRow({ account }: { account: Account }): JSX.Element {
  return (
    <div className="flex items-center gap-2 px-1 py-1.5">
      <span className="shrink-0 font-mono text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
        {account.code}
      </span>
      <span className="text-xs text-foreground truncate">{account.name}</span>
    </div>
  )
}

const SIBLINGS_FILTER_THRESHOLD = 6

function SiblingsPopupBody({ siblings, filter, setFilter }: { siblings: Account[]; filter: string; setFilter: (v: string) => void }): JSX.Element {
  const filtered = filterSiblings(siblings, filter)
  return (
    <>
      <p className="text-xs font-medium text-foreground mb-1.5">
        {siblings.length} code{siblings.length === 1 ? '' : 's'} used
      </p>
      {siblings.length > SIBLINGS_FILTER_THRESHOLD && (
        <input
          className="w-full mb-1.5 px-2 py-1 text-xs rounded border border-input bg-background outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          placeholder="Filter by code or name…"
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      )}
      <div className="flex flex-col max-h-56 overflow-y-auto divide-y divide-border/60">
        {filtered.map(s => <SiblingRow key={s.id} account={s} />)}
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground py-2 text-center">No matches</p>
        )}
      </div>
    </>
  )
}

export function SiblingsPopup({ count, siblings }: { count: number; siblings: Account[] }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const close = (): void => { setIsOpen(false); setFilter('') }
  const style = useSiblingsPopupPosition(isOpen, btnRef, popupRef, close)

  return (
    <>
      <button
        ref={btnRef}
        className="text-xs text-primary hover:underline whitespace-nowrap"
        type="button"
        onClick={() => setIsOpen(v => !v)}
      >
        +{count} more
      </button>
      {isOpen && createPortal(
        <div ref={popupRef} className="bg-popover border border-border rounded-lg shadow-lg el-p-sm w-72 max-w-[85vw]" style={style}>
          <SiblingsPopupBody filter={filter} setFilter={setFilter} siblings={siblings} />
        </div>,
        document.body,
      )}
    </>
  )
}

// ── computeNextSubCode ────────────────────────────────────────────────────────

function chooseDominantSeries(prefixMap: Map<string, number[]>): { bestPrefix: string; bestMax: number } {
  let bestPrefix = ''
  let bestCount = 0
  let bestMax = -1
  for (const [prefix, nums] of prefixMap) {
    const max = Math.max(...nums)
    if (nums.length > bestCount || (nums.length === bestCount && max > bestMax)) {
      bestPrefix = prefix
      bestCount = nums.length
      bestMax = max
    }
  }
  return { bestPrefix, bestMax }
}

function buildPrefixMap(hyphenated: string[]): Map<string, number[]> {
  const prefixMap = new Map<string, number[]>()
  for (const code of hyphenated) {
    const dash = code.lastIndexOf('-')
    const prefix = code.slice(0, dash)
    const num = parseInt(code.slice(dash + 1), 10)
    if (!isNaN(num)) {
      if (!prefixMap.has(prefix)) {prefixMap.set(prefix, [])}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safe after has-check
      prefixMap.get(prefix)!.push(num)
    }
  }
  return prefixMap
}

export function computeNextSubCode(parentCode: string, siblings: Account[]): string | null {
  if (parentCode.includes('-')) {return null}

  const codes = siblings.map(a => a.code).filter((c): c is string => !!c)

  const hyphenated = codes.filter(c => c.includes('-'))
  if (hyphenated.length > 0) {
    const prefixMap = buildPrefixMap(hyphenated)
    if (prefixMap.size > 0) {
      const { bestPrefix, bestMax } = chooseDominantSeries(prefixMap)
      return `${bestPrefix}-${String(bestMax + 1).padStart(Math.max(String(bestMax).length, 3), '0')}`
    }
  }

  const numericCodes = codes.filter(c => /^\d+$/.test(c)).map(c => parseInt(c, 10))
  if (numericCodes.length > 0) {return String(Math.max(...numericCodes) + 1)}

  return `${parentCode}-001`
}

// ── BankDetailsStep + BankIdentifiers ─────────────────────────────────────────

interface BankDetailsStepProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isLoadingBankDetails: boolean
}

export function BankDetailsStep({ formData, setFormData, isLoadingBankDetails }: BankDetailsStepProps): JSX.Element {
  if (isLoadingBankDetails) {
    return <BankDetailsSkeleton currency={formData.currency} />
  }
  return <BankDetailsForm formData={formData} setFormData={setFormData} />
}

function BankDetailsSkeleton({ currency }: { currency: string }): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <div className="grid grid-cols-2 el-gap-field">
        <FormField label="Bank Name"><Skeleton className="h-9 w-full rounded" /></FormField>
        <FormField label="Branch"><Skeleton className="h-9 w-full rounded" /></FormField>
      </div>
      <FormField label="Account Number"><Skeleton className="h-9 w-full rounded" /></FormField>
      <FormField label={getBankCodeLabel(currency)}><Skeleton className="h-9 w-full rounded" /></FormField>
    </div>
  )
}

function BankDetailsForm({ formData, setFormData }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>> }): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <FormField helperText="Account holder name as registered with the bank" label="Name at Bank">
        <Input
          placeholder="e.g., Acme Pvt Ltd"
          value={formData.nameAtBank}
          onChange={e => setFormData(prev => ({ ...prev, nameAtBank: e.target.value }))}
        />
      </FormField>
      <div className="grid grid-cols-2 el-gap-field">
        <FormField required label="Bank Name">
          <Input
            placeholder="e.g., HDFC Bank, Chase, Wise"
            value={formData.bankName}
            onChange={e => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
          />
        </FormField>
        <FormField label="Branch">
          <Input
            placeholder="e.g., Mumbai Main"
            value={formData.branch}
            onChange={e => setFormData(prev => ({ ...prev, branch: e.target.value }))}
          />
        </FormField>
      </div>
      <FormField required label="Account Number">
        <Input
          placeholder="Full account number"
          value={formData.accountNumber}
          onChange={e => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
        />
      </FormField>
      <BankIdentifiers formData={formData} setFormData={setFormData} />
    </div>
  )
}

interface BankIdField {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}

function IfscField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField required helperText="Required for domestic transfers" label="IFSC Code">
      <Input maxLength={11} placeholder="e.g., HDFC0001234" value={formData.ifscCode}
        onChange={e => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))} />
    </FormField>
  )
}

function SwiftField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="For international transfers" label="SWIFT/BIC Code">
      <Input maxLength={11} placeholder="e.g., CHASUS33XXX" value={formData.swiftCode}
        onChange={e => setFormData(prev => ({ ...prev, swiftCode: e.target.value.toUpperCase() }))} />
    </FormField>
  )
}

function RoutingField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="9-digit routing number" label="ABA Routing Number">
      <Input maxLength={9} placeholder="e.g., 021000021" value={formData.routingNumber}
        onChange={e => setFormData(prev => ({ ...prev, routingNumber: e.target.value }))} />
    </FormField>
  )
}

function SortCodeField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <FormField helperText="6-digit sort code" label="Sort Code">
      <Input maxLength={8} placeholder="e.g., 12-34-56" value={formData.sortCode}
        onChange={e => setFormData(prev => ({ ...prev, sortCode: e.target.value }))} />
    </FormField>
  )
}

function IbanField({ formData, setFormData }: BankIdField): JSX.Element {
  return (
    <div className="col-span-2">
      <FormField helperText="International Bank Account Number (up to 34 characters)" label="IBAN">
        <Input maxLength={34} placeholder="e.g., GB29NWBK60161331926819" value={formData.iban}
          onChange={e => setFormData(prev => ({ ...prev, iban: e.target.value.toUpperCase().replace(/\s/g, '') }))} />
      </FormField>
    </div>
  )
}

function BankIdentifiers({ formData, setFormData }: BankIdField): JSX.Element {
  const props: BankIdField = { formData, setFormData }
  return (
    <>
      <div className="border-t border-border el-pt-sm">
        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">
          Bank Identifiers {formData.currency && <span className="font-normal text-muted-foreground">({formData.currency})</span>}
        </h4>
      </div>
      <div className="grid grid-cols-2 el-gap-field">
        {formData.currency === 'INR' && <IfscField {...props} />}
        <SwiftField {...props} />
        {formData.currency === 'USD' && <RoutingField {...props} />}
        {formData.currency === 'GBP' && <SortCodeField {...props} />}
        {['EUR', 'GBP', 'AED'].includes(formData.currency) && <IbanField {...props} />}
      </div>
    </>
  )
}

// ── WizardFooter ──────────────────────────────────────────────────────────────

export interface WizardFooterProps {
  isLastStep: boolean
  isSaving: boolean
  canBack: boolean
  isProceedDisabled: boolean
  onBack: () => void
  onCancel: () => void
  onNext: () => void
}

export function WizardFooter({ isLastStep, isSaving, canBack, isProceedDisabled, onBack, onCancel, onNext }: WizardFooterProps): JSX.Element {
  let nextLabel: string
  if (!isLastStep) {nextLabel = 'Next'}
  else if (isSaving) {nextLabel = 'Creating…'}
  else {nextLabel = 'Create'}
  return (
    <div className="flex justify-between items-center w-full">
      <div>
        {canBack && (
          <Button variant="secondary" onClick={onBack}>
            <span className="inline-block mr-1.5">←</span>
            Back
          </Button>
        )}
      </div>
      <div className="flex el-gap-control-md">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button disabled={isProceedDisabled} onClick={onNext}>{nextLabel}</Button>
      </div>
    </div>
  )
}
